/**
 * One tenant: activate it, brand it, toggle its features.
 *
 * Every editable field is rendered from `GET /api/tenant/v1/catalogue`, which reads
 * config/platform.yaml and config/features.yaml. Adding a themeable colour or a
 * feature flag to those files makes it appear here with no change to this file — the
 * whole point of keeping the catalogue as data.
 *
 * Everything saves to the database via PATCH. Nothing is written to a repository, and
 * nothing needs a deploy.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Card, Pill, Spinner } from '../../shared/ui';
import { openedx } from '../../shared/lib/openedx';
import { tenantsApi, type Tenant, type TenantStatus } from '../api/tenants';
import { ProvidersCard } from './ProvidersCard';
import { FooterCard, type FooterConfig } from './FooterCard';
import { DomainsCard } from './DomainsCard';
import { PaymentsCard } from './PaymentsCard';

interface Catalogue {
  statuses: TenantStatus[];
  footer: FooterConfig;
  branding: { colors: Record<string, string>; typography: Record<string, string>; radius: string };
  features: { name: string; description: string; default: boolean; owner: string }[];
  copy: string[];
  surfaces: string[];
}

export default function TenantDetail() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const justCreated = params.get('created') === '1';
  const queryClient = useQueryClient();

  const tenantQuery = useQuery({
    queryKey: ['tenants', slug],
    queryFn: () => tenantsApi.get(slug),
  });

  const catalogueQuery = useQuery({
    queryKey: ['catalogue'],
    queryFn: () => openedx.get<Catalogue>('/api/tenant/v1/catalogue'),
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => tenantsApi.update(slug, patch as never),
    onSuccess: (updated) => {
      queryClient.setQueryData(['tenants', slug], updated);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });

  if (tenantQuery.isLoading || catalogueQuery.isLoading) {
    return <Spinner label="Loading tenant…" />;
  }

  const tenant = tenantQuery.data;
  const catalogue = catalogueQuery.data;

  if (!tenant || !catalogue) {
    return (
      <Banner tone="danger">
        <strong>Could not load {slug}.</strong> The tenancy service did not respond.
      </Banner>
    );
  }

  return (
    <div className="console__stack console__stack--narrow">
      <nav className="console__crumbs">
        <Link to="/tenants">Tenants</Link> <span aria-hidden="true">/</span> {tenant.name}
      </nav>

      {justCreated && (
        <Banner tone="success">
          <strong>{tenant.name} created.</strong> It is not being served yet — set it to
          active below when you want learners to reach it.
        </Banner>
      )}

      {save.isError && (
        <Banner tone="danger">
          <strong>That change was not saved.</strong> Nothing was written, so it is safe
          to try again.
        </Banner>
      )}

      <StatusCard tenant={tenant} statuses={catalogue.statuses} onSave={save.mutate} busy={save.isPending} />
      <DomainsCard slug={slug} />
      <BrandingCard tenant={tenant} catalogue={catalogue} onSave={save.mutate} busy={save.isPending} />
      <FeaturesCard tenant={tenant} catalogue={catalogue} onSave={save.mutate} busy={save.isPending} />
      <FooterCard
        saved={(tenant as unknown as { footer: FooterConfig | null }).footer ?? null}
        inherited={catalogue.footer ?? {}}
        onSave={save.mutate}
        busy={save.isPending}
      />
      <ProvidersCard slug={slug} />
      {/*
        THE PAID-ENROLMENT FLAG IS PASSED DOWN RATHER THAN FETCHED BY THE CARD.
        `paid_enrollment` is a declared flag in `config/features.yaml` and it is written through
        the tenancy app's own `PATCH /api/tenancy/v1/tenants/<slug>`, which validates against
        that catalogue and refuses an undeclared flag. This component already holds the tenant
        document and the mutation that saves it, so the card gets the value and a writer as
        props — the same `save.mutate` the Features card uses. A payments endpoint that wrote a
        tenancy flag would be a second writer for one field, and the two would diverge on
        validation first and on cache invalidation immediately after.

        `paid_enrollment` is spelled AMERICAN, with two `l`s, because that is the key the
        tenancy app and the pricing app both already use. A `paid_enrolment` here would find
        nothing and report every academy as switched off — the spelling is upstream's, and
        matching it is not a style choice.
      */}
      <PaymentsCard
        slug={slug}
        paidEnrolment={paidEnrolment(tenant, catalogue)}
        paidEnrolmentIsDefault={!('paid_enrollment' in (tenant.features ?? {}))}
        onTogglePaidEnrolment={(next) =>
          save.mutate({ features: { ...(tenant.features ?? {}), paid_enrollment: next } })
        }
        togglingPaidEnrolment={save.isPending}
      />
    </div>
  );
}

type SaveFn = (patch: Record<string, unknown>) => void;

/**
 * The academy's effective `paid_enrollment`, or `undefined` when it cannot be established.
 *
 * `undefined` rather than `false` when the flag is not declared in the catalogue at all,
 * because that is a different fact: an academy inheriting a documented default of `false` is
 * switched off, and a build whose catalogue has lost the flag cannot say what the academy is.
 * The card renders the two differently and offers a toggle only for the first — a switch that
 * flips a value nobody could read writes a guess over whatever is really there.
 */
function paidEnrolment(tenant: Tenant, catalogue: Catalogue): boolean | undefined {
  const saved = tenant.features ?? {};
  if ('paid_enrollment' in saved) return Boolean(saved.paid_enrollment);
  const declared = catalogue.features.find((flag) => flag.name === 'paid_enrollment');
  return declared ? Boolean(declared.default) : undefined;
}

/**
 * Status.
 *
 * Activation is one click because it is the common case, but the destructive
 * direction is deliberately a separate, differently-coloured action — flipping a
 * tenant off is not symmetric with turning it on.
 */
function StatusCard({
  tenant,
  statuses,
  onSave,
  busy,
}: {
  tenant: Tenant;
  statuses: TenantStatus[];
  onSave: SaveFn;
  busy: boolean;
}) {
  const isActive = tenant.status === 'active';

  return (
    <Card title="Status">
      <dl className="console__facts">
        <dt>Serving</dt>
        <dd>
          <Pill tone={tenant.status}>{tenant.status}</Pill>
        </dd>
        <dt>Domain</dt>
        <dd className="mono">{tenant.domains.site}</dd>
        <dt>Organisations</dt>
        <dd className="mono">{tenant.orgs.join(', ')}</dd>
      </dl>

      <div className="console__form-actions" style={{ marginTop: 'var(--space-4)' }}>
        {!isActive && (
          <Button variant="primary" busy={busy} onClick={() => onSave({ status: 'active' })}>
            Activate
          </Button>
        )}
        {isActive && !tenant.is_primary && (
          <Button variant="danger" busy={busy} onClick={() => onSave({ status: 'suspended' })}>
            Suspend
          </Button>
        )}
        {statuses.includes('planned') && tenant.status === 'suspended' && (
          <Button busy={busy} onClick={() => onSave({ status: 'planned' })}>
            Move back to planned
          </Button>
        )}
      </div>

      {!isActive && (
        <p className="console__hint">
          While it is not active, this domain returns &ldquo;no academy registered&rdquo; rather
          than falling back to another tenant.
        </p>
      )}
    </Card>
  );
}

/**
 * Branding.
 *
 * The colour list comes from the catalogue, so it is exactly the set of tokens the
 * stylesheet actually consumes — an input here always corresponds to something real.
 * Unset values show the platform default and are stored as absent rather than as a
 * copy, so a later change to the default still reaches this tenant.
 */
function BrandingCard({
  tenant,
  catalogue,
  onSave,
  busy,
}: {
  tenant: Tenant;
  catalogue: Catalogue;
  onSave: SaveFn;
  busy: boolean;
}) {
  const saved = useMemo(
    () => ((tenant.branding as { colors?: Record<string, string> })?.colors ?? {}),
    [tenant.branding],
  );
  const [colors, setColors] = useState<Record<string, string>>(saved);

  // Re-sync when a save returns, so the inputs reflect what the server stored rather
  // than what was typed.
  useEffect(() => setColors(saved), [saved]);

  const tokens = Object.keys(catalogue.branding.colors);
  const dirty = JSON.stringify(colors) !== JSON.stringify(saved);

  return (
    <Card
      title="Branding"
      action={
        dirty ? (
          <Button
            variant="primary"
            busy={busy}
            onClick={() => onSave({ branding: { colors } })}
          >
            Save colours
          </Button>
        ) : undefined
      }
    >
      <div className="console__swatches">
        {tokens.map((token) => {
          const inherited = catalogue.branding.colors[token] ?? '#000000';
          const value = colors[token] ?? inherited;
          const isSet = token in colors;

          return (
            <div className="console__swatch" key={token}>
              <label htmlFor={`c-${token}`}>{token}</label>
              <div className="console__swatch-row">
                <input
                  id={`c-${token}`}
                  type="color"
                  value={value}
                  onChange={(e) => setColors({ ...colors, [token]: e.target.value })}
                />
                <input
                  className="console__hex"
                  value={value}
                  aria-label={`${token} hex`}
                  onChange={(e) => setColors({ ...colors, [token]: e.target.value })}
                />
                {isSet ? (
                  <button
                    type="button"
                    className="ui-btn"
                    data-variant="ghost"
                    onClick={() => {
                      const next = { ...colors };
                      delete next[token];
                      setColors(next);
                    }}
                  >
                    Reset
                  </button>
                ) : (
                  <span className="console__inherited">inherited</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="console__hint">
        Saved to the database and applied at the next page load. One build serves every
        tenant, so there is nothing to rebuild or deploy.
      </p>
    </Card>
  );
}

/** Feature flags, listed from the catalogue with their descriptions. */
function FeaturesCard({
  tenant,
  catalogue,
  onSave,
  busy,
}: {
  tenant: Tenant;
  catalogue: Catalogue;
  onSave: SaveFn;
  busy: boolean;
}) {
  const saved = tenant.features ?? {};

  function toggle(name: string, next: boolean) {
    onSave({ features: { ...saved, [name]: next } });
  }

  return (
    <Card title="Features">
      <ul className="console__flags">
        {catalogue.features.map((flag) => {
          const value = flag.name in saved ? saved[flag.name] : flag.default;
          return (
            <li key={flag.name}>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(value)}
                  disabled={busy}
                  onChange={(e) => toggle(flag.name, e.target.checked)}
                />
                <span className="console__flag-name">{flag.name}</span>
              </label>
              <p>{flag.description}</p>
              {!(flag.name in saved) && <span className="console__inherited">default</span>}
            </li>
          );
        })}
      </ul>
      {catalogue.features.length === 0 && (
        <p className="console__hint">
          No flags are declared. Add them to <code>config/features.yaml</code> and they
          appear here.
        </p>
      )}
    </Card>
  );
}
